// @flow

// List of CSS properties that should preserve numeric values as pixels
const PIXEL_PROPERTIES = [
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'width', 'height', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight',
  'top', 'right', 'bottom', 'left',
  'borderRadius', 'borderWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'fontSize', 'letterSpacing',
  'gap', 'rowGap', 'columnGap'
];

function combineStyles(...styles) {
  const res = function CombineStyles(theme) {
    const outStyles = styles.map((arg) => {
      // Apply the "theme" object for style functions.
      if (typeof arg === 'function') {
        return arg(theme);
      }
      // Objects need no change.
      return arg;
    });

    return outStyles.reduce((acc, val) => Object.assign(acc, val));
  };  
  return res;
}

/**
 * Converts numeric values to pixel strings for specific CSS properties
 * @param {Object} styles - Style object to process
 * @return {Object} Style object with numeric values converted to pixel strings
 */
function convertNumericValues(styles) {
  if (!styles || typeof styles !== 'object') {
    return styles;
  }
  
  const result = { ...styles };
  
  // Process each property
  Object.keys(result).forEach(key => {
    const value = result[key];

    // Skip CSS at-rules like @media, @keyframes, @supports
    // These should be preserved as-is and their nested values processed separately
    if (key.startsWith('@')) {
      // Still process the nested values within the at-rule
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[key] = convertNumericValues(value);
      }
      return;
    }

    // If the value is an object (nested styles), process recursively
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = convertNumericValues(value);
    } else if (PIXEL_PROPERTIES.includes(key) && typeof value === 'number') {
      // If this is a pixel property and value is a number, convert to pixel string
      result[key] = `${value}px`;
    } else if (Array.isArray(value)) {
      // Handle arrays for things like margin: [10, 20]
      if (PIXEL_PROPERTIES.includes(key)) {
        result[key] = value.map(item =>
          typeof item === 'number' ? `${item}px` : item
        );
      }
    }
  });
  
  return result;
}

/**
 * Creates a classes object by applying theme to style functions
 * @param {Object} styles - Object containing style functions
 * @param {Object} theme - Theme object to pass to style functions
 * @param {Object} options - Additional options to pass to style functions
 * @param {Object} specialOptions - Special options for specific styles { styleName: { options } }
 * @return {Object} Object with computed styles
 */
function createClasses(
  styles, 
  theme, 
  options = {},
  specialOptions = {}
) {
  return Object.keys(styles).reduce((acc, key) => {
    const styleFunc = styles[key];
    
    if (typeof styleFunc === 'function') {
      // Check if this style has special options
      let computedStyle;
      if (specialOptions[key]) {
        computedStyle = styleFunc(theme, specialOptions[key]);
      } else {
        computedStyle = styleFunc(theme, options);
      }
      
      // Convert numeric values to pixel strings for specific CSS properties
      acc[key] = convertNumericValues(computedStyle);
    } else {
      acc[key] = convertNumericValues(styleFunc);
    }
    
    return acc;
  }, {});
}

export { createClasses, convertNumericValues };
export default combineStyles;